import { useEffect, useRef, useState } from "react";
import NextLink from "next/link";
import { useRouter } from "next/compat/router";
import {
  Box,
  Button,
  IconFa,
  Badge,
  ErrorText,
  Link,
  PopOver,
} from "@cruk/cruk-react-components";
import {
  faFlag,
  faSterlingSign,
  faHeartPulse,
  faComment,
  faEdit,
  faShareAlt,
  faBars,
} from "@fortawesome/free-solid-svg-icons";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";

import { useTracking } from "@fwa/src/hooks/useTracking";
import { fetcher, FWA_BASE_URL } from "@fwa/src/services/apiClient";
import { fwsUrlFeedItemDelete } from "@fwa/src/services/feedItemService";
import { userNameForPage } from "@fwa/src/utils/pageUtils";
import { getPathFromUrl, queryAsString } from "@fwa/src/utils/urlUtils";
import { imagePathFromImageType } from "@fwa/src/services/imageService";
import { useFundraiserContext } from "@fwa/src/contexts/FundraiserContext";
import { reportFormUrl } from "@fwa/src/services/reportFormService";

import { FormActions } from "@fwa/src/components/FormActions";
import { FeedItemDonationForm } from "@fwa/src/components/FeedItemDonationForm";
import { FeedItemPostForm } from "@fwa/src/components/FeedItemPostForm";
import { FeedItemStrava } from "@fwa/src/components/FeedItemStrava";
import { FeedItemFitbit } from "@fwa/src/components/FeedItemFitbit";
import { FeedItemMilestone } from "@fwa/src/components/FeedItemMilestone";
import { Share } from "@fwa/src/components/Share";

import sharedStyles from "@fwa/src/components/styles.module.css";
import styles from "@fwa/src/components/FeedItem/styles.module.css";
import { isBrowser } from "@fwa/src/utils/browserUtils";

import type {
  FeedItemType,
  FeedItemStatusType,
  FeedItemMilestoneType,
  FeedItemDonationRealType,
  FeedItemDonationFakeType,
  FeedItemDonationFacebookType,
  FeedItemPostType,
  FeedItemActivityStravaType,
  FeedItemActivityFitbitType,
} from "@fwa/src/types";

const crukCLogo = "/assets/images/logos/cruk_c_128.png";

const CommentBadge = () => (
  <span className={styles["icon-badge-span"]}>
    <Badge backgroundColor="tertiary" size="s">
      <IconFa faIcon={faComment} size="xs" color="text-light" />
    </Badge>
  </span>
);
const FlagBadge = () => (
  <span className={styles["icon-badge-span"]}>
    <Badge backgroundColor="secondary" size="s">
      <IconFa faIcon={faFlag} size="xs" color="text-light" />
    </Badge>
  </span>
);
const ActivityBadge = () => (
  <span className={styles["icon-badge-span"]}>
    <Badge backgroundColor="sports-activity" size="s">
      <IconFa faIcon={faHeartPulse} size="xs" color="text-light" />
    </Badge>
  </span>
);
const PoundBadge = () => (
  <span className={styles["icon-badge-span"]}>
    <Badge backgroundColor="primary" size="s">
      <IconFa faIcon={faSterlingSign} size="xs" />
    </Badge>
  </span>
);

export function FeedItem({
  feedItem,
  handleDelete,
  handleEdit,
  pageUrl,
  pageId,
  isStravaMetric,
  fitbitDailyTargetSteps,
  showParentPageLink = false,
  isDonationConfirmation = false,
}: {
  feedItem: FeedItemType;
  handleDelete: (updatedItem: FeedItemType) => void;
  handleEdit: (updatedItem: FeedItemType) => void;
  pageUrl: string | null;
  pageId: string;
  isStravaMetric: boolean;
  fitbitDailyTargetSteps: number;
  showParentPageLink?: boolean;
  isDonationConfirmation?: boolean;
}) {
  const { trackError, trackEventGtm } = useTracking();
  const [error, setError] = useState<Error | null>(null);
  const [status, setStatus] = useState<FeedItemStatusType>("idle");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const router = useRouter();
  const isDonationConfirmationPath = !!router?.asPath?.includes(
    "/donation/confirmation",
  );
  const isFeedItemSharePath = !!router?.query?.["feed"];
  const isPathDonorEditable = isDonationConfirmationPath || isFeedItemSharePath;
  const [secret, setSecret] = useState<string | undefined>(undefined);
  const userName = userNameForPage(feedItem?.page);
  const displayNameOrUserName = feedItem?.page?.owner?.displayName
    ? feedItem.page.owner.displayName
    : userName;

  const [fundraiserState] = useFundraiserContext();
  const { fundraiser } = fundraiserState;

  const isOwner: boolean =
    isDonationConfirmation || // if you see the donation confirmation assume that you are the donor can edit donation feed item
    (fundraiser && // logged in fundraiser is the page owner where feed item belongs
      fundraiser?.uniqueId === feedItem.page?.fundraiser?.uniqueId) ||
    false;

  const isEditable: boolean =
    (feedItem.entityType === "DonationReal" &&
      !!secret &&
      isFeedItemSharePath &&
      isPathDonorEditable) || // Donor editing their donation from email link
    (isOwner &&
      (feedItem.entityType === "Post" ||
        (feedItem.entityType === "DonationReal" &&
          "originatorPaymentId" in feedItem) || // Donor is editing their donation
        (feedItem.entityType === "DonationFake" &&
          "online" in feedItem &&
          !feedItem.online))) || // Ignore Facebook donations possibly
    false;

  const isDeletable =
    isOwner &&
    (feedItem.entityType === "Post" ||
      feedItem.entityType === "Milestone" ||
      feedItem.entityType === "ActivityStrava" ||
      (feedItem.entityType === "DonationFake" &&
        "online" in feedItem &&
        !feedItem.online));

  // Avatar URL start ///////////////////////

  // Order of preference
  // 1) special kickstarter fake donations and milestones have hard coded image urls because of an issue with uploading them in legacy admin.
  // 2) special fake donations and milstones sometimes have a profileImage URLs defined in the feedItem data.
  // 3) Real donations from the owner might be missing a profile image in the donation so lets use their account profile image url as the feeditem avatar url
  // 4) Real donations from everyone else has avatarUrl as 'undefined' letting the avatar image component derrive a default letter image from avatar name

  const pageFundraiserProfileImage =
    !feedItem?.page?.owner && feedItem?.page?.fundraiser?.profileImage
      ? feedItem.page.fundraiser.profileImage.entityType === "ImageFacebook"
        ? `${feedItem.page.fundraiser.profileImage.url}?width=300&height=300`
        : imagePathFromImageType(feedItem.page.fundraiser?.profileImage)
      : undefined;

  // now this is grim donation.profileImage is a string and milestone.profileImage is an ImageType
  const feedItemAsDonation:
    | FeedItemDonationRealType
    | FeedItemDonationFakeType
    | FeedItemDonationFacebookType
    | undefined =
    feedItem.entityType === "DonationReal" ||
    feedItem.entityType === "DonationFake"
      ? (feedItem as FeedItemDonationRealType | FeedItemDonationFakeType)
      : undefined;
  const feedItemAsDonationFake: FeedItemDonationFakeType | undefined =
    feedItem.entityType === "DonationFake"
      ? (feedItem as FeedItemDonationFakeType)
      : undefined;
  const feedItemAsMilestone: FeedItemMilestoneType | undefined =
    feedItem.entityType === "Milestone"
      ? (feedItem as FeedItemMilestoneType)
      : undefined;

  // special cases where FWA forces the avatar image
  const kickStartAvatarImages = {
    Tesco: "https://fundraise.cancerresearchuk.org/images/tescosquare.png",
    "Standard Life":
      "https://fundraise.cancerresearchuk.org/images/standardlifeletterssquare.jpg",
    "Facebook donation":
      "https://fundraise.cancerresearchuk.org/images/facebook-circle.png",
  };

  const kickStarterDonationName =
    (feedItemAsDonationFake?.donationName?.trim() || "") in
    kickStartAvatarImages
      ? (feedItemAsDonationFake?.donationName?.trim() as keyof typeof kickStartAvatarImages)
      : undefined;

  const kickStartAvatarUrl = kickStarterDonationName
    ? kickStartAvatarImages[kickStarterDonationName]
    : undefined;

  const milestoneAvatarUrl = feedItemAsMilestone ? crukCLogo : undefined;

  const forcedAvatarUrl = kickStartAvatarUrl || milestoneAvatarUrl;

  const feedItemProfileImageUrl =
    forcedAvatarUrl ||
    feedItemAsDonation?.profileImage ||
    feedItemAsMilestone?.profileImage?.url;

  const isDonerAndOwner =
    "donationName" in feedItem &&
    feedItemAsDonation?.donationName === displayNameOrUserName;
  const donationProfileImageUrl = isDonerAndOwner
    ? pageFundraiserProfileImage
    : feedItemProfileImageUrl; // undefined means it uses the default images with letters

  const avatarUrl = feedItemAsDonation
    ? donationProfileImageUrl
    : feedItemProfileImageUrl || pageFundraiserProfileImage;

  // Avatar URL end ///////////////////////

  const parentPageLink =
    showParentPageLink && feedItem.page.entityType === "FundraisingPage" ? (
      <NextLink
        href={`/page${feedItem.page.url.slice(
          feedItem.page.url.lastIndexOf("/"),
        )}?feed=${feedItem.uniqueId}`}
        data-cta-type="link-strava-check-account"
      >
        <Link as="span">{feedItem.page.title}</Link>
      </NextLink>
    ) : undefined;

  const handleCancelClick = () => {
    setStatus("idle");
  };

  const handleDeleteClick = () => {
    setStatus("deleting");
    setError(null);

    const url = fwsUrlFeedItemDelete(feedItem);
    fetcher(url, {
      method: "DELETE",
    })
      .then((res) => res as Response)
      .then((res: Response) => {
        if (res && !res.ok) {
          setError(new Error("Unable to delete"));
          return undefined;
        }
        trackEventGtm({
          event: "feeditem_delete",
          entityType: feedItem.entityType,
        });
        if (handleDelete) {
          handleDelete(feedItem);
        }
        return undefined;
      })
      .catch((err: Error) => {
        trackError(err, { component: "FeedItem" });
        setStatus("idle");
        setError(new Error("Unable to delete"));
      });
  };

  const handleEditData = (data: Partial<FeedItemType>) => {
    setStatus("idle");
    handleEdit({ ...feedItem, ...data });
    trackEventGtm({
      event: "feeditem_edit",
      entityType: feedItem.entityType,
    });
  };

  // If we have a secret in the query string and we don't have it in state
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (router?.query?.["secret"] && !secret) {
        setSecret(queryAsString(router.query["secret"]));
      }
    }, 0);
    return () => clearTimeout(timeout);
  }, [router, secret]);

  // once we have it in state remove secret from query string
  useEffect(() => {
    if (router?.query?.["secret"] && secret) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { secret: secretInQuery, slug, ...rest } = router.query;
      router
        .push(
          {
            pathname: router.asPath.split("?")[0],
            query: rest,
          },
          undefined,
          {
            shallow: true,
          },
        )
        .catch((err) => {
          trackError(err as Error, { component: "FeedItem" });
        });
    }
  }, [router, secret, trackError]);

  const renderItem = (pageUrl: string) => {
    switch (feedItem.entityType) {
      case "ActivityStrava":
        return (
          <FeedItemStrava
            feedItem={feedItem as FeedItemActivityStravaType}
            avatarUrl={avatarUrl}
            displayName={displayNameOrUserName}
            isMetric={isStravaMetric}
          />
        );
      case "ActivityFitbit":
        return (
          <FeedItemFitbit
            feedItem={feedItem as FeedItemActivityFitbitType}
            avatarUrl={avatarUrl}
            displayName={displayNameOrUserName}
            fitbitDailyTargetSteps={fitbitDailyTargetSteps}
          />
        );
      case "Post":
        return (
          <Box>
            <FeedItemPostForm
              feedItem={feedItem as FeedItemPostType}
              avatarUrl={avatarUrl}
              displayName={displayNameOrUserName}
              status={status}
              handleCancelClick={handleCancelClick}
              handleEditData={handleEditData}
              parentPageLink={parentPageLink}
            />
          </Box>
        );
      case "Milestone":
        return (
          <div>
            <FeedItemMilestone
              feedItem={feedItem as FeedItemMilestoneType}
              avatarUrl={avatarUrl}
            />
          </div>
        );
      case "DonationReal":
        return (
          <FeedItemDonationForm
            feedItem={feedItem as FeedItemDonationRealType}
            avatarUrl={avatarUrl}
            handleCancelClick={handleCancelClick}
            handleEditData={handleEditData}
            status={status}
            parentPageLink={parentPageLink}
            pageUrl={pageUrl}
            pageId={pageId}
            secret={secret}
          />
        );
      case "DonationFacebook":
        return (
          <FeedItemDonationForm
            feedItem={feedItem as FeedItemDonationFacebookType}
            avatarUrl={avatarUrl}
            handleCancelClick={handleCancelClick}
            handleEditData={handleEditData}
            status={status}
            parentPageLink={parentPageLink}
            pageUrl={pageUrl}
            pageId={pageId}
          />
        );
      case "DonationFake":
        return (
          <FeedItemDonationForm
            feedItem={feedItem as FeedItemDonationFakeType}
            avatarUrl={avatarUrl}
            handleCancelClick={handleCancelClick}
            handleEditData={handleEditData}
            status={status}
            parentPageLink={parentPageLink}
            pageUrl={pageUrl}
            pageId={pageId}
          />
        );
      default:
        return null;
    }
  };

  const isHighlighted = status === "editing" || status === "deleting";

  // Keep the native <dialog> element's open state in sync with isHighlighted,
  // position it over the item it is editing/deleting, and lock background scroll
  useEffect(() => {
    if (!isBrowser) return undefined;

    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    if (!isHighlighted) {
      if (dialog.open) dialog.close();
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;

    const positionDialog = () => {
      const wrapperRect = wrapperRef.current?.getBoundingClientRect();
      if (!wrapperRect) return;
      dialog.style.width = `${wrapperRect.width}px`;
      dialog.style.left = `${wrapperRect.left}px`;

      // measure height only after the width is set, since changing the width can reflow
      // the content and change its height, which would otherwise push the dialog off-screen
      const maxTop = window.innerHeight - dialog.getBoundingClientRect().height;
      dialog.style.top = `${Math.max(0, Math.min(wrapperRect.top, maxTop))}px`;
    };

    if (!dialog.open) {
      dialog.showModal();
      positionDialog();
      // stop scroll on the body while the dialog is open, but allow scroll on the dialog itself
      document.body.style.overflow = "hidden";
    }

    window.addEventListener("resize", positionDialog);

    // content can change size while open (e.g. delete confirmation swapping in), so
    // reposition whenever that happens rather than only on window resize
    const resizeObserver = new ResizeObserver(positionDialog);
    if (dialog.firstElementChild)
      resizeObserver.observe(dialog.firstElementChild);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      window.removeEventListener("resize", positionDialog);
      resizeObserver.disconnect();
    };
  }, [isHighlighted]);

  const feedItemBadges = {
    Post: <CommentBadge />,
    Milestone: <FlagBadge />,
    ActivityStrava: <ActivityBadge />,
    ActivityFitbit: <ActivityBadge />,
    DonationReal: <PoundBadge />,
    DonationFake: <PoundBadge />,
    DonationFacebook: <PoundBadge />,
  };

  const feedItemContent = ({ showBadge }: { showBadge: boolean }) => (
    <div className={styles["feed-item-wrapper"]}>
      {showBadge ? feedItemBadges[feedItem.entityType] : null}

      {pageUrl ? renderItem(pageUrl) : null}

      {error ? <ErrorText>{error.message}</ErrorText> : null}

      {status === "deleting" && (
        <FormActions
          submitLabel="Delete"
          onCancel={handleCancelClick}
          onSubmit={handleDeleteClick}
          submitButtonType="button"
        />
      )}

      {status === "idle" && (
        <div
          className={styles["action-button-wrapper"]}
          data-component="feed-item-footer"
        >
          <div className={sharedStyles["row"]} style={{ gap: "1em" }}>
            {isEditable ? (
              <Button
                appearance="tertiary"
                className="edit-link component-button"
                role="button"
                tabIndex={0}
                onClick={() => {
                  setStatus("editing");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setStatus("editing");
                  }
                }}
                data-cta-type="edit-feed-item"
              >
                <IconFa faIcon={faEdit} />
                Edit
              </Button>
            ) : null}
            <Share
              isOwner={isEditable}
              page={feedItem.page}
              feedItem={feedItem}
              ctaLocation="feedItem"
            >
              <Button
                type="button"
                appearance="tertiary"
                data-cta-type="open-share"
              >
                <IconFa faIcon={faShareAlt} />
                Share
              </Button>
            </Share>
          </div>
          <PopOver
            style={{ minWidth: "14em" }}
            modalContent={
              <div
                className={styles["feeditem-menu-actions"]}
                data-component="feed-item-popover"
              >
                {isDeletable ? (
                  <Button
                    appearance="tertiary"
                    className="delete-link component-button"
                    onClick={() => {
                      setStatus("deleting");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setStatus("deleting");
                      }
                    }}
                    data-cta-type="delete-feed-item"
                  >
                    <IconFa faIcon={faTrashCan} />
                    Delete
                  </Button>
                ) : null}

                <a
                  target="_blank"
                  rel="noreferrer"
                  href={reportFormUrl({
                    issueUrl: `${FWA_BASE_URL}${getPathFromUrl(
                      feedItem.page.url,
                    )}?feed=${feedItem.uniqueId}`,
                  })}
                  data-cta-type="report-feed-item"
                >
                  <Button appearance="tertiary" tabIndex={-1}>
                    <IconFa faIcon={faFlag} />
                    Report content
                  </Button>
                </a>
              </div>
            }
            modalLabel="share options"
            position="bottom"
          >
            <Button
              type="button"
              appearance="tertiary"
              data-cta-type="open-post-menu"
            >
              <IconFa faIcon={faBars} />
              Menu
            </Button>
          </PopOver>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div ref={wrapperRef}>{feedItemContent({ showBadge: true })}</div>
      <dialog
        ref={dialogRef}
        className={styles["feedItemDialog"]}
        aria-label={`${status} feed item modal`}
        onCancel={(e) => {
          e.preventDefault();
          handleCancelClick();
        }}
        onClick={(e) => {
          // dialog element itself is the backdrop, its content isn't
          if (e.target === dialogRef.current) handleCancelClick();
        }}
      >
        {/* badge is pinned to the dialog's own box, rather than scrolling with its content */}
        <span className={styles["dialogIconBadge"]}>
          {feedItemBadges[feedItem.entityType]}
        </span>
        {feedItemContent({ showBadge: false })}
      </dialog>
    </>
  );
}

export default FeedItem;
